import { formatEuro, getOvereenkomstLabel } from './accountsShared';
import { hasCoordinates } from './mapData';

// DJB-241: the per-pin info card on Accounts › Maps. Everything in here is
// plain data + DOM (no Leaflet, no React) so the colour rules, the last-seen
// dot and the maps deeplink are unit-testable; MachineMap.js wires the card
// into a Leaflet popup and owns the hover / tap behaviour.
//
// Input is one /machines/map-data row (DJB-238): {machineId, name, city,
// province, plan, cash, mobile (cents of the latest billing period),
// daysSinceCashout (null when never cashed out), lastSeen (LocalDateTime
// tuple), latitude, longitude}.
//
// DJB-257: rows also carry openDays (null = days unknown, or 7 booleans
// Monday → Sunday, true = open) and todayStatus ('open' | 'closed' |
// 'unknown', computed server-side in Europe/Brussels).

// cash above this → the cash line + amount turn yellow ("veel op te halen")
export const CASH_ALERT_CENTS = 150 * 100;
// more days than this since the last cashout → the days figure turns orange
export const DAYS_ALERT = 90;
// last seen within this window = online (same rule as the dashboard widgets)
export const ONLINE_WINDOW_MINUTES = 60;

export const isCashAlert = (cents) => Number(cents) > CASH_ALERT_CENTS;

export const isDaysAlert = (days) =>
  days != null && Number.isFinite(Number(days)) && Number(days) > DAYS_ALERT;

// LocalDateTime arrives as [y, m, d, h, min, s?, nanos?] (Jackson default) —
// also accept an ISO string or an epoch number for robustness.
export const parseServerDateTime = (value) => {
  if (value == null || value === '') return null;
  if (Array.isArray(value)) {
    if (value.length < 3) return null;
    const [y, m, d, h = 0, min = 0, s = 0, nanos = 0] = value.map(Number);
    if (![y, m, d].every(Number.isFinite)) return null;
    return new Date(y, m - 1, d, h, min, s, Math.floor(nanos / 1e6));
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const isOnline = (lastSeen, now = new Date()) => {
  const seen = parseServerDateTime(lastSeen);
  if (!seen) return false;
  const diffMinutes = (now - seen) / (1000 * 60);
  return diffMinutes <= ONLINE_WINDOW_MINUTES;
};

const pad2 = (n) => String(n).padStart(2, '0');

export const formatDateTime = (value) => {
  const date = parseServerDateTime(value);
  if (!date) return '—';
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()} ${pad2(
    date.getHours()
  )}:${pad2(date.getMinutes())}`;
};

// "zonet" / "12 min geleden" / "3 u geleden" / "5 d geleden"
export const formatRelative = (value, now = new Date()) => {
  const date = parseServerDateTime(value);
  if (!date) return 'nooit gezien';
  const minutes = Math.max(0, Math.floor((now - date) / (1000 * 60)));
  if (minutes < 1) return 'zonet';
  if (minutes < 60) return `${minutes} min geleden`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} u geleden`;
  const days = Math.floor(hours / 24);
  return `${days} d geleden`;
};

export const formatDays = (days) => {
  if (days == null || !Number.isFinite(Number(days))) return '—';
  return `${Math.max(0, Math.round(Number(days)))} d`;
};

export const formatPlace = (machine) =>
  [machine.city, machine.province].filter((part) => part && String(part).trim()).join(', ') ||
  '—';

export const formatCoordinates = (machine) =>
  `${Number(machine.latitude).toFixed(5)}, ${Number(machine.longitude).toFixed(5)}`;

// Apple devices get an Apple Maps link (iOS hands https://maps.apple.com
// straight to the Maps app, macOS to Maps.app); everything else gets the
// Google Maps directions URL, which Android routes into the Google Maps app.
export const isAppleDevice = (userAgent) =>
  /iPhone|iPad|iPod|Macintosh/i.test(String(userAgent || ''));

export const mapsDirectionsUrl = (machine, userAgent) => {
  if (!hasCoordinates(machine)) return null;
  const destination = `${Number(machine.latitude)},${Number(machine.longitude)}`;
  if (isAppleDevice(userAgent)) {
    const query = machine.name ? `&q=${encodeURIComponent(machine.name)}` : '';
    return `https://maps.apple.com/?daddr=${destination}${query}`;
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
};

// DJB-257: open days, same rules as the Accounts info card (DJB-252): seven
// dots Ma → Zo, open green / closed red; unknown days all grey with a
// "Dagen niet gekend" caption. Today (Europe/Brussels) gets a thin ring.
export const OPEN_DAY_LABELS = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
export const OPEN_DAYS_UNKNOWN_LABEL = 'Dagen niet gekend';

export const hasKnownOpenDays = (openDays) =>
  Array.isArray(openDays) && openDays.length === OPEN_DAY_LABELS.length;

const BRUSSELS_WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const getBrusselsTodayIndex = (date = new Date()) =>
  BRUSSELS_WEEKDAYS.indexOf(
    new Intl.DateTimeFormat('en-US', { timeZone: 'Europe/Brussels', weekday: 'short' }).format(date)
  );

// anything missing or unexpected reads as unknown
export const normalizeTodayStatus = (todayStatus) => {
  const status = String(todayStatus || '').toLowerCase();
  return status === 'open' || status === 'closed' ? status : 'unknown';
};

export const isOpenToday = (machine) => normalizeTodayStatus(machine && machine.todayStatus) === 'open';

const el = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
};

const figure = (key, label, value, alert) => {
  const wrap = el('div', `machine-pin-card-figure is-${key}${alert ? ' is-alert' : ''}`);
  wrap.appendChild(el('dt', 'machine-pin-card-figure-label', label));
  wrap.appendChild(el('dd', 'machine-pin-card-figure-value', value));
  return wrap;
};

const openDaysBlock = (openDays, todayIndex) => {
  const known = hasKnownOpenDays(openDays);
  const wrap = el('div', `machine-pin-card-open-days${known ? '' : ' is-unknown'}`);
  const dots = el('div', 'machine-pin-card-open-days-dots');
  dots.setAttribute('role', 'group');
  dots.setAttribute('aria-label', 'Openingsdagen');
  OPEN_DAY_LABELS.forEach((label, index) => {
    const state = !known ? 'unknown' : openDays[index] ? 'open' : 'closed';
    const isToday = index === todayIndex;
    const dot = el('span', `open-day-dot ${state}${isToday ? ' is-today' : ''}`, label);
    dot.dataset.state = state;
    dot.title = `${label}: ${
      state === 'open' ? 'open' : state === 'closed' ? 'gesloten' : 'niet gekend'
    }${isToday ? ' (vandaag)' : ''}`;
    if (isToday) dot.setAttribute('aria-current', 'date');
    dots.appendChild(dot);
  });
  wrap.appendChild(dots);
  if (!known) wrap.appendChild(el('p', 'machine-pin-card-open-days-unknown', OPEN_DAYS_UNKNOWN_LABEL));
  return wrap;
};

export const EXPANDED_CLASS = 'is-expanded';

// DJB-248: the pill under the maps link that puts the machine in the
// Planning panel (PlanningPanel.js). Once the machine is in the planning the
// pill reads "In planning" and is disabled — a location sits in the planning
// at most once; removing is done in the panel.
export const ADD_TO_PLANNING_LABEL = 'Add to planning';
export const IN_PLANNING_LABEL = 'In planning';
export const PLANNED_CLASS = 'is-planned';

export const setPinCardPlanned = (card, planned) => {
  if (!card) return;
  const button = card.querySelector('.machine-pin-card-planning');
  if (!button) return;
  const on = Boolean(planned);
  button.classList.toggle(PLANNED_CLASS, on);
  button.disabled = on;
  button.setAttribute('aria-pressed', on ? 'true' : 'false');
  button.querySelector('.machine-pin-card-planning-text').textContent = on
    ? IN_PLANNING_LABEL
    : ADD_TO_PLANNING_LABEL;
  button.title = on ? 'Deze locatie staat al in de planning' : 'Voeg deze locatie toe aan de planning';
};

// Builds the card DOM. The compact part (name · place · plan · cash · mobile ·
// days + the last-seen dot + the DJB-257 open-days dots) is always visible; `.machine-pin-card-extra` is
// revealed by the `is-expanded` class (toggled by MachineMap.js on hover/tap).
// `onAddToPlanning(machine)` wires the DJB-248 pill; `isPlanned(machineId)`
// gives its initial state (MachineMap.js re-syncs it on every open).
export const buildPinCard = (
  machine,
  { now = new Date(), userAgent, onAddToPlanning, isPlanned } = {}
) => {
  const ua = userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent : '');
  const online = isOnline(machine.lastSeen, now);
  const cashAlert = isCashAlert(machine.cash);
  const daysAlert = isDaysAlert(machine.daysSinceCashout);

  const card = el('article', 'machine-pin-card');
  card.dataset.machineId = machine.machineId || '';
  card.setAttribute('aria-expanded', 'false');

  // head: dot · name · plan
  const head = el('header', 'machine-pin-card-head');
  const dot = el(
    'span',
    `machine-pin-card-status ${online ? 'is-online' : 'is-offline'}`
  );
  dot.setAttribute('role', 'img');
  const seenLabel = `${online ? 'Online' : 'Offline'} · laatst gezien ${formatRelative(
    machine.lastSeen,
    now
  )}`;
  dot.setAttribute('aria-label', seenLabel);
  dot.title = seenLabel;
  head.appendChild(dot);
  head.appendChild(el('h4', 'machine-pin-card-name', machine.name || 'Naamloze machine'));
  head.appendChild(el('span', 'machine-pin-card-plan', getOvereenkomstLabel(machine.plan)));
  card.appendChild(head);

  card.appendChild(el('div', 'machine-pin-card-place', machine.city || '—'));

  // figures: cash (yellow when > €150) · mobile (neutral) · days (orange > 90)
  const figures = el('dl', 'machine-pin-card-figures');
  figures.appendChild(figure('cash', 'Cash', formatEuro(machine.cash), cashAlert));
  figures.appendChild(figure('mobile', 'Mobile', formatEuro(machine.mobile), false));
  figures.appendChild(figure('days', 'Days', formatDays(machine.daysSinceCashout), daysAlert));
  card.appendChild(figures);

  // DJB-257: open days under the figures, visible on hover already
  card.appendChild(openDaysBlock(machine.openDays, getBrusselsTodayIndex(now)));

  // extra (expand): last seen, province, address link → maps app, coordinates
  const extra = el('div', 'machine-pin-card-extra');
  const seenRow = el('div', 'machine-pin-card-row');
  seenRow.appendChild(el('span', 'machine-pin-card-row-label', 'Laatst gezien'));
  seenRow.appendChild(
    el(
      'span',
      'machine-pin-card-row-value',
      `${formatDateTime(machine.lastSeen)} · ${formatRelative(machine.lastSeen, now)}`
    )
  );
  extra.appendChild(seenRow);

  const cashoutRow = el('div', 'machine-pin-card-row');
  cashoutRow.appendChild(el('span', 'machine-pin-card-row-label', 'Laatste lichting'));
  cashoutRow.appendChild(
    el(
      'span',
      'machine-pin-card-row-value',
      machine.daysSinceCashout == null
        ? 'nog nooit'
        : `${formatDays(machine.daysSinceCashout)} geleden`
    )
  );
  extra.appendChild(cashoutRow);

  const url = mapsDirectionsUrl(machine, ua);
  if (url) {
    const link = el('a', 'machine-pin-card-route');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.title = 'Route naar deze machine openen in de kaarten-app';
    link.appendChild(el('span', 'machine-pin-card-route-icon', '➤'));
    const text = el('span', 'machine-pin-card-route-text');
    text.appendChild(el('span', 'machine-pin-card-route-address', formatPlace(machine)));
    text.appendChild(el('span', 'machine-pin-card-route-coords', formatCoordinates(machine)));
    link.appendChild(text);
    link.appendChild(el('span', 'machine-pin-card-route-cta', 'Route'));
    extra.appendChild(link);
  }

  // DJB-248: "Add to planning" pill under the maps link
  if (typeof onAddToPlanning === 'function') {
    const button = el('button', 'machine-pin-card-planning');
    button.type = 'button';
    button.appendChild(el('span', 'machine-pin-card-planning-icon', '+'));
    button.appendChild(el('span', 'machine-pin-card-planning-text', ADD_TO_PLANNING_LABEL));
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (button.disabled) return;
      onAddToPlanning(machine);
      setPinCardPlanned(card, true);
    });
    extra.appendChild(button);
    setPinCardPlanned(card, typeof isPlanned === 'function' && isPlanned(machine.machineId));
  }
  card.appendChild(extra);

  const hint = el('span', 'machine-pin-card-hint');
  hint.setAttribute('aria-hidden', 'true');
  card.appendChild(hint);

  return card;
};

export const setPinCardExpanded = (card, expanded) => {
  if (!card) return;
  card.classList.toggle(EXPANDED_CLASS, Boolean(expanded));
  card.setAttribute('aria-expanded', expanded ? 'true' : 'false');
};

export const isPinCardExpanded = (card) => Boolean(card && card.classList.contains(EXPANDED_CLASS));
